import './lists.css';

/**
 * Numbered pager for history tables (00-ds-09 §3, D148): "1–50 of 2,431",
 * Previous / Next, page numbers — not infinite scroll, so people can return to
 * "page 3 of October" and share links (the page lives in the URL, §6). UI-only;
 * the server does keyset paging + the total count (§8).
 */
export function NumberedPager({
  page,
  pageSize = 50,
  total,
  approxTotal = false,
  onPage,
}: {
  /** 1-based current page. */
  page: number;
  pageSize?: number;
  total: number;
  /** Over 10,000 rows the count may be approximate ("of about 12,000"). */
  approxTotal?: boolean;
  onPage: (page: number) => void;
}) {
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const clamped = Math.min(Math.max(1, page), pageCount);
  const from = total === 0 ? 0 : (clamped - 1) * pageSize + 1;
  const to = Math.min(clamped * pageSize, total);
  const totalLabel = `${approxTotal ? 'about ' : ''}${total.toLocaleString()}`;

  const nums = pageWindow(clamped, pageCount);

  return (
    <div className="lx-pager">
      <span className="lx-pager-range">
        <b>
          {from.toLocaleString()}–{to.toLocaleString()}
        </b>{' '}
        of {totalLabel}
      </span>
      <div className="lx-pager-nav">
        <button type="button" className="lx-pager-btn" disabled={clamped <= 1} onClick={() => { onPage(clamped - 1); }}>
          Previous
        </button>
        {nums.map((n, i) =>
          n === 'gap' ? (
            <span key={`gap${String(i)}`} className="lx-pager-ellipsis" aria-hidden>
              …
            </span>
          ) : (
            <button
              key={n}
              type="button"
              className={`lx-pager-btn${n === clamped ? ' on' : ''}`}
              aria-current={n === clamped ? 'page' : undefined}
              onClick={() => { onPage(n); }}
            >
              {n}
            </button>
          ),
        )}
        <button type="button" className="lx-pager-btn" disabled={clamped >= pageCount} onClick={() => { onPage(clamped + 1); }}>
          Next
        </button>
      </div>
    </div>
  );
}

/** First, last, current ±1, with gaps — e.g. 1 … 4 5 6 … 49. */
function pageWindow(page: number, pageCount: number): (number | 'gap')[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i + 1);
  const out: (number | 'gap')[] = [1];
  const start = Math.max(2, page - 1);
  const end = Math.min(pageCount - 1, page + 1);
  if (start > 2) out.push('gap');
  for (let n = start; n <= end; n++) out.push(n);
  if (end < pageCount - 1) out.push('gap');
  out.push(pageCount);
  return out;
}
